import { checkCensusEligibility, createCensusAccount } from "@/api/census-account-client";
import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { fetchCensusIdentity } from "@/hooks/use-census-session";
import { Lock, Mail } from "@cakewalk/ui/icons";
import {
  V2Button,
  V2Card,
  V2CardContent,
  V2FormActions,
  V2FormField,
  V2Input,
  V2Typography,
} from "@cakewalk/ui/v2";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { type FormEvent, useState } from "react";
import { OtpResend } from "./OtpResend";

/**
 * "Create an account" step for company-census.
 *
 * We already hold the owner's details, so this collects NOTHING but email +
 * password — it only provisions a Cognito login. It's OWNER-ONLY and gated
 * server-side: we first ask the public eligibility endpoint whether the email is
 * an existing owner; only then does the platform API create the Cognito user
 * (pulling name / phone / company from our records). Because the owner's phone is
 * set + verified during creation, the immediate sign-in is challenged for an SMS
 * code, which we confirm inline (same as the login step). If Cognito returns a
 * different next step (e.g. TOTP setup), we point the user at the main app to
 * finish, then come back and log in.
 */

export function CompanyCensusCreateAccountStep({
  defaultEmail,
  playBuild = false,
  ceremony = null,
  onCreated,
  onBack,
}: {
  defaultEmail?: string;
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onCreated: (email: string, canManage: boolean) => void;
  onBack: () => void;
}) {
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [password, setPassword] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [maskedDestination, setMaskedDestination] = useState<string | undefined>(undefined);
  // SMS codes can be re-sent; authenticator-app codes can't.
  const [otpViaSms, setOtpViaSms] = useState(false);

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  const finishLogin = async () => {
    const identity = await fetchCensusIdentity();
    onCreated(email.trim(), identity.canManage);
  };

  const startSignIn = async () => {
    const response = await signIn({ username: email.trim(), password });
    if (response.isSignedIn) {
      await finishLogin();
      return;
    }
    const step = response.nextStep?.signInStep;
    if (step === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
      setMaskedDestination(response.nextStep.codeDeliveryDetails?.destination);
      setOtpViaSms(true);
      setAwaitingOtp(true);
      return;
    }
    if (step === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
      setMaskedDestination("your authenticator app");
      setOtpViaSms(false);
      setAwaitingOtp(true);
      return;
    }
    setNotice(
      "Your account is ready. Finish setting up two-factor on the main Cakewalk app, then come back and log in.",
    );
  };

  // Re-send the SMS by restarting the sign-in (the account already exists, so
  // this never re-creates it). <OtpResend> owns the cooldown and only calls this
  // once its countdown hits zero: resolve to restart it, throw to stay armed.
  const resendCode = async () => {
    setError(undefined);
    try {
      const response = await signIn({ username: email.trim(), password });
      if (response.isSignedIn) {
        await finishLogin();
        return;
      }
      if (response.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
        setMaskedDestination(response.nextStep.codeDeliveryDetails?.destination);
        setOtp("");
        return;
      }
      setError("We couldn't resend the code. Try again.");
      throw new Error(`resend-unexpected-step:${response.nextStep?.signInStep ?? "unknown"}`);
    } catch (err) {
      if (err instanceof Error && err.name === "UserAlreadyAuthenticatedException") {
        await finishLogin();
        return;
      }
      setError((prev) => prev ?? (err instanceof Error ? err.message : "Failed to resend code"));
      throw err;
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError(undefined);
    setNotice(undefined);
    try {
      // Owner-only gate: the email must already exist as the business owner.
      const eligibility = await checkCensusEligibility(email.trim());
      if (!eligibility.exists || !eligibility.isOwner) {
        setError(
          "We couldn't match this email to a business owner on file. Only the business owner can create a login here — please use the email on your Cakewalk account, or contact support.",
        );
        return;
      }

      // Cognito-only: email + password. Everything else comes from our records.
      await createCensusAccount({ email: email.trim(), password });

      await startSignIn();
    } catch (err) {
      if (err instanceof Error && err.name === "UserAlreadyAuthenticatedException") {
        await finishLogin();
        return;
      }
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await confirmSignIn({ challengeResponse: otp.replace(/\D/g, "") });
      if (result.isSignedIn) {
        await finishLogin();
        return;
      }
      setError(`Authentication incomplete (${result.nextStep?.signInStep ?? "UNKNOWN_STEP"}).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to verify code");
    } finally {
      setIsLoading(false);
    }
  };

  if (awaitingOtp) {
    return (
      // Key each screen so it mounts a FRESH subtree. Without this, React reuses
      // the same <form> DOM node across screens (create → otp), and the browser
      // treats that form's autofill as already handled — suppressing the SMS
      // one-time-code suggestion on the OTP field. Same fix as the activation
      // step (PR #150) and the login step, which key their screens too.
      <div key="otp" className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
        <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
          <V2CardContent className="flex flex-col gap-6 p-6">
            <div className="flex flex-col gap-2">
              <V2Typography variant="overline" className="text-cw-v2-reliant-red">
                Verify it&rsquo;s you
              </V2Typography>
              <V2Typography variant="heading-medium" as="h1" className="font-semibold">
                Enter your verification code
              </V2Typography>
              <V2Typography variant="body" muted>
                We sent a code to {maskedDestination ?? "your device"}. Enter it below to finish
                creating your account.
              </V2Typography>
            </div>

            <form onSubmit={handleOtpSubmit} className="flex flex-col gap-5">
              <V2FormField label="Verification code" error={error}>
                <V2Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  data-sensitive
                />
              </V2FormField>
              <V2FormActions variant="stretch">
                <V2Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={isLoading || otp.replace(/\D/g, "").length === 0}
                  data-track="button-company-census-create-verify"
                >
                  Verify and continue
                </V2Button>
              </V2FormActions>
            </form>

            {otpViaSms ? (
              <div className="flex justify-center">
                <OtpResend onResend={resendCode} dataTrack="button-company-census-create-resend" />
              </div>
            ) : null}
          </V2CardContent>
        </V2Card>
      </div>
    );
  }

  return (
    <div key="signup" className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
      <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
        <V2CardContent className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-2">
            <V2Typography
              variant="overline"
              {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
            >
              Create your login
            </V2Typography>
            <V2Typography
              variant="heading-medium"
              as="h1"
              {...mergeStepBuildProps(titleMotion, { className: "font-semibold" })}
            >
              Set up your account
            </V2Typography>
            <V2Typography variant="body" muted {...mergeStepBuildProps(descriptionMotion)}>
              We already have your business details — just pick a password. For security, only the
              business owner can create a login, and we&rsquo;ll text your two-factor code to the
              number on file.
            </V2Typography>
          </div>

          {notice ? (
            <V2Typography variant="body" className="text-cw-v2-reliant-red">
              {notice}
            </V2Typography>
          ) : null}

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5"
            data-track="form-company-census-create-account"
          >
            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony))}>
              <V2FormField label="Email">
                <V2Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  prefix={<Mail className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>
            </div>

            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 1, ceremony))}>
              <V2FormField label="Password" error={error}>
                <V2Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  prefix={<Lock className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>
            </div>

            <V2FormActions
              variant="stretch"
              {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 2, ceremony))}
            >
              <V2Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={isLoading || email.trim().length === 0 || password.length === 0}
                data-track="button-company-census-create-submit"
              >
                {isLoading ? "Creating your account…" : "Create account"}
              </V2Button>
            </V2FormActions>
          </form>

          <div className="flex flex-col items-center gap-1 border-t border-cw-v2-border/60 pt-5">
            <V2Typography variant="meta" muted>
              Already have an account?
            </V2Typography>
            <V2Button
              variant="link"
              onClick={onBack}
              data-track="button-company-census-create-back-to-login"
            >
              Back to login
            </V2Button>
          </div>
        </V2CardContent>
      </V2Card>

      <div className="flex items-center justify-center gap-2 text-cw-v2-text-muted">
        <Lock className="h-3.5 w-3.5" aria-hidden />
        <V2Typography variant="meta" muted>
          Encrypted &amp; secure. Your login works across Cakewalk.
        </V2Typography>
      </div>
    </div>
  );
}
